import { listenInWorldFrame } from './worldFrame';

/**
 * The Quikbase controls tour's keys (owner round 14.6: "whatever button is being highlighted, only that is allowed and if
 * they press other buttons, it should kind of show some greyed out animation"; "pressing all buttons bring person to
 * same place or nearly same"; Sol dev-port D2 A). While armed, the page decides which movement keys reach the world:
 *
 *   allow null      every key reaches the world as usual (walking into the portal)
 *   allow 'W'...    only that key; each press becomes one fixed step, so W/S and A/D bring the learner back
 *   allow 'none'    no movement key (the look and fly steps)
 *
 * A real key the world must not see is stopped before Unity reads it (a capture listener on the page and inside the
 * world's same-origin frame); a step is the world's own key events, sent by the page, with a key-up after a fixed time.
 * Measured on DEV 2026-10-11: a blocked D held 0.9 s moved nothing (1.1% of the view changed, idle 0.7%); W 300 ms then
 * S 420 ms came back within 0.7% of the view, A 300 ms then D 300 ms within 0.9% (walking back is slower).
 */
export const MOVE_KEYS = {
  KeyW: 'W', ArrowUp: 'W',
  KeyA: 'A', ArrowLeft: 'A',
  KeyS: 'S', ArrowDown: 'S',
  KeyD: 'D', ArrowRight: 'D',
  Space: 'Space',
};
const SEND = {
  W: { code: 'KeyW', key: 'w', keyCode: 87 },
  A: { code: 'KeyA', key: 'a', keyCode: 65 },
  S: { code: 'KeyS', key: 's', keyCode: 83 },
  D: { code: 'KeyD', key: 'd', keyCode: 68 },
  Space: { code: 'Space', key: ' ', keyCode: 32 },
};
export const STEP_MS = { W: 300, A: 300, S: 420, D: 300, Space: 150 };

let armed = false;
let allow = null;
const subscribers = new Set();
let detach = null;
let busy = {};

const notify = event => subscribers.forEach(fn => fn(event));
/** Listen for 'pressed' / 'denied' / 'seen' (a key that went through, with nothing decided) - the on-screen pad shows them. */
export function onKeyGate(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function worldTarget() {
  try {
    const frame = typeof document !== 'undefined' ? document.getElementById('world-iframe') : null;
    const w = frame && frame.contentWindow;
    const d = w && w.document;
    if (!d) return null;
    return { w, el: d.querySelector('canvas') || d.body };
  } catch (_) {
    return null;
  }
}

function send(type, name) {
  const target = worldTarget();
  if (!target) return;
  const { code, key, keyCode } = SEND[name];
  const event = new target.w.KeyboardEvent(type, { code, key, keyCode, which: keyCode, bubbles: true, cancelable: true });
  target.el.dispatchEvent(event);
}

/** One fixed step of a key (the on-screen pad, or an allowed real key). Ignored while that key's step is still running. */
export function pressKey(name) {
  if (!SEND[name] || busy[name]) return;
  busy[name] = true;
  send('keydown', name);
  setTimeout(() => {
    send('keyup', name);
    busy[name] = false;
  }, STEP_MS[name] || 300);
  notify({ type: 'pressed', name });
}

const typing = target => target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName || ''));

function onKey(event) {
  if (!armed || !event.isTrusted || typing(event.target)) return;
  const name = MOVE_KEYS[event.code];
  if (!name) return;
  if (allow === null) {
    if (event.type === 'keydown' && !event.repeat) notify({ type: 'seen', name });
    return;
  }
  // the world never sees this real key: either it becomes one fixed step, or it is refused
  event.preventDefault();
  event.stopImmediatePropagation();
  if (event.type !== 'keydown' || event.repeat) return;
  if (allow === name) pressKey(name);
  else notify({ type: 'denied', name });
}

/** Arms (or disarms) the gate and sets which key may move the learner. */
export function setKeyGate(next) {
  armed = Boolean(next && next.armed);
  allow = armed ? (next.allow === undefined ? null : next.allow) : null;
  if (armed && !detach && typeof window !== 'undefined') {
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    const stopFrame = listenInWorldFrame({ keydown: onKey, keyup: onKey }, { everyMs: 500 });
    detach = () => {
      stopFrame();
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
    };
  }
  if (!armed && detach) {
    detach();
    detach = null;
    busy = {};
  }
}

export const keyGateAllows = () => (armed ? allow : null);
